// localizedListings.ts — geo-based secondary-listings merge for the web widget.
//
// Mirrors the platform-neutral core reference (SDK core/src/localized-listings.ts):
// config/state resolution, {state} URL templating, everyNth dispersion, and the
// every-Nth de-duped shuffle merge. The widget supplies its own fetch + the
// CloudFront-derived state (userLocation.state.code, seeded off the primary
// listings fetch via appendViewerHeaders). A 404 on the templated URL (a state
// we have no data for) is a normal skip.

import { IListing } from '../providers/types'

/** Raw config object as it arrives from the CMS (theme.localizedListings). */
export interface LocalizedListingsConfig {
  enabled?: boolean
  source?: string
  baseUrl?: string
  urlTemplate?: string
  frequency?: number
  forceState?: string
}

/** A fully-resolved localized-listings integration (config validated). */
export interface LocalizedListingsIntegration {
  source?: string
  baseUrl: string
  urlTemplate: string
  /** Dispersion percent (25 → every 4th slot). */
  frequency: number
  /** Forced state (2-letter upper), or undefined. */
  forceState?: string
}

function nonEmpty(v: unknown): string | undefined {
  return typeof v === 'string' && v.trim().length > 0 ? v.trim() : undefined
}

function numeric(v: unknown): number | undefined {
  if (typeof v === 'number' && Number.isFinite(v)) return v
  if (typeof v === 'string') {
    const n = Number(v)
    if (Number.isFinite(n)) return n
  }
  return undefined
}

/** Normalize to a 2-letter uppercase state/region code, or undefined. */
export function normState(v: unknown): string | undefined {
  const s = nonEmpty(v)
  if (!s) return undefined
  // CloudFront viewer-country-region can be "GA" or a longer subdivision; take
  // the trailing 2-letter alpha token when present, else the raw upper value.
  const code = s.toUpperCase()
  return /^[A-Z]{2}$/.test(code) ? code : (code.match(/[A-Z]{2}$/)?.[0] ?? code)
}

/**
 * Resolve the active integration from the theme config object (may be a JSON
 * string if it slipped through un-parsed). Returns null when disabled or
 * missing a baseUrl/urlTemplate.
 */
export function resolveLocalizedListings(
  raw: LocalizedListingsConfig | string | undefined | null,
): LocalizedListingsIntegration | null {
  let cfg: LocalizedListingsConfig | undefined
  if (typeof raw === 'string') {
    try {
      const p = JSON.parse(raw)
      if (p && typeof p === 'object') cfg = p as LocalizedListingsConfig
    } catch {
      /* not JSON */
    }
  } else if (raw && typeof raw === 'object') {
    cfg = raw
  }

  if (!cfg) return null
  if (cfg.enabled === false) return null // explicit off; absent = on (presence implies intent)

  const baseUrl = nonEmpty(cfg.baseUrl)
  const urlTemplate = nonEmpty(cfg.urlTemplate)
  if (!baseUrl || !urlTemplate) return null

  return {
    source: nonEmpty(cfg.source),
    baseUrl,
    urlTemplate,
    frequency: numeric(cfg.frequency) ?? 0,
    forceState: normState(cfg.forceState),
  }
}

/** Forced state wins, then the resolved geo state, else null. */
export function resolveState(
  integration: LocalizedListingsIntegration,
  geoState: string | null | undefined,
): string | null {
  return integration.forceState ?? normState(geoState) ?? null
}

/** Build the cache URL by filling `{state}` (lowercased) into the template. */
export function buildCacheUrl(integration: LocalizedListingsIntegration, state: string): string {
  const path = integration.urlTemplate.replace(/\{state\}/gi, state.toLowerCase())
  const base = integration.baseUrl
  if (base.endsWith('/') && path.startsWith('/')) return base + path.slice(1)
  if (!base.endsWith('/') && !path.startsWith('/')) return base + '/' + path
  return base + path
}

/**
 * Slots between localized listings for a given percent. 25 → 4 (every 4th slot).
 * 0/absent → 0 (disabled); >=100 → 1 (every slot).
 */
export function everyNth(frequencyPercent: number): number {
  if (!frequencyPercent || frequencyPercent <= 0) return 0
  if (frequencyPercent >= 100) return 1
  return Math.max(1, Math.round(100 / frequencyPercent))
}

/** Fisher-Yates shuffle using an injectable RNG (deterministic in tests). */
function shuffle<T>(arr: T[], rng: () => number): T[] {
  const a = arr.slice()
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1))
    ;[a[i], a[j]] = [a[j], a[i]]
  }
  return a
}

/**
 * Replace every Nth slot of `primary` with a localized listing, keeping the
 * total count unchanged. Secondary listings are first de-duped against primary
 * `id`s, then shuffled (random pick "from whatever was returned"), then cycled
 * so every Nth slot is filled. Returns `primary` unchanged when there's nothing
 * to disperse.
 */
export function mergeEveryNth(
  primary: IListing[],
  secondary: IListing[],
  everyN: number,
  rng: () => number = Math.random,
): IListing[] {
  if (everyN <= 0 || secondary.length === 0 || primary.length === 0) return primary
  const primaryIds = new Set(primary.map((l) => String(l.id)))
  const pool = shuffle(
    secondary.filter((l) => l && l.id != null && !primaryIds.has(String(l.id))),
    rng,
  )
  if (pool.length === 0) return primary

  const out: IListing[] = []
  let s = 0
  for (let i = 0; i < primary.length; i++) {
    if ((i + 1) % everyN === 0) {
      out.push(pool[s % pool.length])
      s++
    } else {
      out.push(primary[i])
    }
  }
  return out
}
